import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlMatchResult, UrlMatcher, UrlSegment } from '@angular/router';
import { StatusMainPageComponent } from './status-main-page.component';
import { StatusMainPageManageSubscriptionComponent } from './manage-subscription/status-main-page-manage-subscription.component';
import { StatusEntityRedirectComponent } from './redirects/status-entity-redirect.component';
import { StatusVerifyRedirectComponent } from './redirects/status-verify-redirect.component';
import { StatusAuthGuard } from '../../../_theme/core/users/Auth/_services/status-auth.guard';
import { AuthGuard } from '../../../_theme/core/users/Auth/_services/auth.guard';
import { StatusMainPageSharedModule } from './status-main-page-shared.module';
import { isAppHost } from './core/status-main-page.url-resolver';

const customDomainManageRouteMatcher: UrlMatcher = (segments: UrlSegment[]): UrlMatchResult | null => {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return null;
  }

  if (isAppHost(window.location.hostname) || segments.length !== 1) {
    return null;
  }

  return String(segments[0]?.path || '').trim().toLowerCase() === 'manage'
    ? { consumed: segments }
    : null;
};

/**
 * Public and internal routing map for the status-main-page experience.
 * Routes support:
 * - public slug pages
 * - short-link incident/maintenance focused views
 * - internal authenticated status page views
 */
const routes: Routes = [
  // Legacy login entry points collapse to canonical status routes.
  {
    path: 'login',
    redirectTo: '',
    pathMatch: 'full'
  },
  {
    path: ':id/login',
    redirectTo: ':id',
    pathMatch: 'full'
  },
  // Public focused-entity routes (incident / maintenance) without explicit org slug.
  {
    path: 'incidents/:incidentId',
    component: StatusMainPageComponent,
    data: { mode: 'public' }
  },
  {
    path: 'maintenance/:maintenanceId',
    component: StatusMainPageComponent,
    data: { mode: 'public' }
  },
  // Custom-domain SMS short links resolve through the same public redirect APIs as app-host links.
  {
    path: 'i/:statusId/:incidentId',
    component: StatusEntityRedirectComponent,
    data: { redirectSource: 'short-link', entityType: 'incident' }
  },
  {
    path: 'm/:statusId/:maintenanceId',
    component: StatusEntityRedirectComponent,
    data: { redirectSource: 'short-link', entityType: 'maintenance' }
  },
  // Public landing view for a resolved status page.
  {
    path: '',
    component: StatusMainPageComponent,
    data: { mode: 'public' },
    pathMatch: 'full'
  },
  // Internal status page by slug, protected by authenticated org access.
  {
    path: 'internal/:slug',
    component: StatusMainPageComponent,
    canActivate: [AuthGuard],
    data: { mode: 'internal' }
  },
  // Canonical custom-domain manage route.
  {
    matcher: customDomainManageRouteMatcher,
    component: StatusMainPageManageSubscriptionComponent,
    canActivate: [StatusAuthGuard],
    data: { mode: 'public', manageLinkSupported: true }
  },
  // Manage-like routes stay on the invalid-link screen when unsupported.
  {
    path: 'manage',
    component: StatusMainPageManageSubscriptionComponent,
    canActivate: [StatusAuthGuard],
    data: { mode: 'public', manageLinkSupported: false }
  },
  {
    path: 'subscriptions/manage',
    component: StatusMainPageManageSubscriptionComponent,
    canActivate: [StatusAuthGuard],
    data: { mode: 'public', manageLinkSupported: false }
  },
  // Public slug routes with access guard and focused-entity variants.
  {
    path: ':id/incidents/:incidentId',
    component: StatusMainPageComponent,
    canActivate: [StatusAuthGuard],
    data: { mode: 'public' }
  },
  {
    path: ':id/maintenance/:maintenanceId',
    component: StatusMainPageComponent,
    canActivate: [StatusAuthGuard],
    data: { mode: 'public' }
  },
  {
    path: ':id/subscriptions/manage',
    component: StatusMainPageManageSubscriptionComponent,
    canActivate: [StatusAuthGuard],
    data: { mode: 'public', manageLinkSupported: false }
  },
  {
    path: ':id/manage',
    component: StatusMainPageManageSubscriptionComponent,
    canActivate: [StatusAuthGuard],
    data: { mode: 'public', manageLinkSupported: false }
  },
  {
    path: ':id',
    component: StatusMainPageComponent,
    canActivate: [StatusAuthGuard],
    data: { mode: 'public' }
  },
  // Any unknown public route in this feature shows the invalid-link state.
  {
    path: '**',
    component: StatusVerifyRedirectComponent,
    data: {
      invalidMessage: 'This public status page link is invalid or unsupported.'
    }
  }
];

/**
 * Feature module that wires status-main-page routing and shared UI dependencies.
 */
@NgModule({
  imports: [
    StatusMainPageComponent,
    StatusMainPageManageSubscriptionComponent,
    StatusMainPageSharedModule,
    RouterModule.forChild(routes)
  ]
})
export class StatusMainPageModule { }
